import { t } from '@/utils/i18n'
import { useState, useEffect, useMemo } from 'react'
import { useMount } from 'ahooks'
import { DatePickerView, Divider, Button } from 'antd-mobile'
import dayjs from 'dayjs'
import { OptionItem } from '@/types/interfaces'
import styles from './index.module.less'
import { utils } from '@/utils'
import {
  dateSelectTags,
  defaultDateTag,
  DEFAULT_MIN_DATE,
  DEFAULT_MAX_DATE,
} from './config'

const { px2rem, getToday, getThisMonth, getThisQuarter, getThisYear, computedDateTag } = utils

export interface Dates {
  startDate: Date
  endDate: Date
}

export interface Times {
  startTime: number
  endTime: number
}

interface Iprops {
  value?: Times
  showShortSelect?: boolean
  onCancel?(): void
  onConfirm?(data: Times): void
}

function KpRangeDate(props: Iprops) {
  const { value, showShortSelect = true, onCancel, onConfirm } = props
  const now = new Date()
  const [dateTag, setDateTag] = useState<string | null>(value ? computedDateTag(value) : defaultDateTag!.value)
  const [isStartDate, setIsStartDate] = useState(true)
  const [startDate, setStartDate] = useState<Date>(now)
  const [endDate, setEndDate] = useState<Date>(now)
  const startDateLabel = useMemo(
    () => dayjs(startDate).format('YYYY-MM-DD'),
    [startDate]
  )
  const endDateLabel = useMemo(
    () => dayjs(endDate).format('YYYY-MM-DD'),
    [endDate]
  )
  const activeDate = isStartDate ? startDate : endDate

  useEffect(() => {
    if (!value) return
    const { startTime, endTime } = value
    setStartDate(new Date(Number(startTime)))
    setEndDate(new Date(Number(endTime)))
  }, [value])

  useMount(() => {
    if (!showShortSelect || !dateTag) return
    handleDateTagToTime({ value: dateTag })
  })

  function handleDateTagToTime(tag: OptionItem) {
    let _startDate = startDate
    let _endDate = endDate
    if (tag.value === 'today') {
      ({ startDate: _startDate, endDate: _endDate } = getToday())
    } else if (tag.value === 'thisMonth') {
      ({ startDate: _startDate, endDate: _endDate } = getThisMonth())
    } else if (tag.value === 'thisQuarter') {
      ({ startDate: _startDate, endDate: _endDate } = getThisQuarter())
    } else if (tag.value === 'thisYear') {
      ({ startDate: _startDate, endDate: _endDate } = getThisYear())
    }
    setStartDate(_startDate)
    setEndDate(_endDate)
    return {
      startDate: _startDate,
      endDate: _endDate,
    }
  }

  function onShortTagClick(tag: OptionItem) {
    setDateTag(tag.value)
    const { startDate: _startDate, endDate: _endDate } =
      handleDateTagToTime(tag)
    onConfirm?.({
      startTime: _startDate.getTime(),
      endTime: _endDate.getTime(),
    })
  }

  // 日期选项变化
  function onDateChange(value: Date) {
    if (isStartDate) {
      setStartDate(value)
    } else {
      setEndDate(new Date(new Date(value).getTime() + 24 * 60 * 60 * 1000 - 1))
    }
    setDateTag(null)
  }

  function onDateCancel() {
    onCancel?.()
  }

  function onDateConfirm() {
    onConfirm?.({
      startTime: startDate.getTime(),
      endTime: endDate.getTime(),
    })
  }

  return (
    <div className={styles['range-date-section']}>
      {showShortSelect && (
        <>
          <div className={styles['short-tag-box']}>
            {!!dateSelectTags.length &&
              dateSelectTags.map((item) => (
                <div
                  onClick={() => onShortTagClick(item)}
                  className={`${styles['short-tag']} ${
                    item.value === dateTag ? styles['selected-short-tag'] : ''
                  }`}
                  key={item.value}
                >
                  {item.label}
                </div>
              ))}
          </div>
          <Divider
            style={{
              color: '#7f7f7f',
              borderColor: '#7f7f7f',
            }}
          >
            {t('自定义时间')}
          </Divider>
        </>
      )}

      <div className={styles['date-type-box']}>
        <span
          className={`${styles['start-time']} ${
            isStartDate ? styles['time-type-active'] : ''
          }`}
          onClick={() => setIsStartDate(true)}
        >
          {startDateLabel}
        </span>
        <span className={styles['to']}>{t('至')}</span>
        <span
          className={`${styles['end-time']} ${
            !isStartDate ? styles['time-type-active'] : ''
          }`}
          onClick={() => setIsStartDate(false)}
        >
          {endDateLabel}
        </span>
      </div>
      <DatePickerView
        defaultValue={activeDate}
        value={activeDate}
        style={{
          '--height': `${px2rem(240)}`,
          '--item-height': `${px2rem(80)}`,
        }}
        min={isStartDate ? DEFAULT_MIN_DATE : startDate}
        max={isStartDate ? endDate : DEFAULT_MAX_DATE}
        onChange={onDateChange}
      />

      <div className={styles['dropdown-btn-box']}>
        <Button
          color="default"
          className={styles['cancel-btn']}
          onClick={onDateCancel}
        >
          {t('取消')}
        </Button>
        <Button
          color="primary"
          className={styles['confirm-btn']}
          onClick={onDateConfirm}
        >
          {t('确认')}
        </Button>
      </div>
    </div>
  )
}

export default KpRangeDate
